import { useState } from 'react'
import { ArrowRight, Smartphone, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useWebPush } from '@/hooks/use-web-push'
import { useAuth } from '@/lib/auth'
import { buildNotificationSettingsUrl } from '@/lib/arena'
import { browserNotificationRuntime } from '@/lib/browser-notification-runtime'
import {
  homeScreenNotificationSetupState,
  type HomeScreenNotificationSetupState,
} from '@/lib/home-screen-notification-state'
import { useI18n } from '@/lib/i18n'
import type { InstallationPlatformHint } from '@/lib/installation-guidance'

export const HOME_SCREEN_NOTICE_DISMISSED_KEY
  = 'mentiss-app-notification-setup-dismissed-v1'

function readDismissed(): boolean {
  try {
    return localStorage.getItem(HOME_SCREEN_NOTICE_DISMISSED_KEY) === 'true'
  }
  catch {
    return false
  }
}

function supportsHomeScreenNotificationNotice(
  platform: InstallationPlatformHint,
): boolean {
  return platform === 'ios' || platform === 'android'
}

export function HomeScreenNotificationNotice({
  platform,
  setupState,
  settingsUrl,
}: {
  platform: InstallationPlatformHint
  setupState: HomeScreenNotificationSetupState
  settingsUrl: string
}) {
  const { t } = useI18n()
  const [dismissed, setDismissed] = useState(readDismissed)

  const handleDismiss = () => {
    setDismissed(true)
    try {
      localStorage.setItem(HOME_SCREEN_NOTICE_DISMISSED_KEY, 'true')
    }
    catch {}
  }

  if (
    !supportsHomeScreenNotificationNotice(platform)
    || dismissed
    || setupState !== 'needs-setup'
  ) {
    return null
  }

  return (
    <aside
      aria-labelledby="home-screen-notification-notice-title"
      className="mb-4 rounded-lg border border-amber-200/70 bg-amber-50/80 p-4 text-sm text-amber-900 shadow-sm dark:border-amber-700/50 dark:bg-amber-950/30 dark:text-amber-100"
      data-testid="home-screen-notification-notice"
    >
      <div className="flex items-start gap-3">
        <Smartphone
          aria-hidden="true"
          className="mt-0.5 size-5 flex-shrink-0 text-amber-600 dark:text-amber-400"
        />
        <div className="min-w-0 flex-1">
          <h2
            id="home-screen-notification-notice-title"
            className="font-semibold"
          >
            {t('homeScreenNotice.title')}
          </h2>
          <p className="mt-1 leading-relaxed">
            {t('homeScreenNotice.description')}
          </p>
          <Button
            asChild
            className="mt-3 min-h-11 whitespace-normal"
            size="sm"
            variant="outline"
          >
            <a href={settingsUrl}>
              {t('homeScreenNotice.cta')}
              <ArrowRight aria-hidden="true" className="ml-2 size-4" />
            </a>
          </Button>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="flex-shrink-0 rounded-md p-2 transition-colors hover:bg-amber-200/50 dark:hover:bg-amber-800/50"
          aria-label={t('homeScreenNotice.dismiss')}
        >
          <X aria-hidden="true" className="size-4" />
        </button>
      </div>
    </aside>
  )
}

function RegisteredHomeScreenNotificationNotice({
  expectedUserId,
  expectedSessionId,
  platform,
  settingsUrl,
}: {
  expectedUserId: string
  expectedSessionId: string
  platform: InstallationPlatformHint
  settingsUrl: string
}) {
  const viewModel = useWebPush(expectedUserId, expectedSessionId)
  return (
    <HomeScreenNotificationNotice
      platform={platform}
      setupState={homeScreenNotificationSetupState(
        viewModel.state,
        viewModel.statePending,
      )}
      settingsUrl={settingsUrl}
    />
  )
}

export function GameNotificationSetupNotice() {
  const { authResolved, user, browserSessionId } = useAuth()
  const { language } = useI18n()
  const { platform } = browserNotificationRuntime.readInstallationHints()
  const settingsUrl = buildNotificationSettingsUrl(language)

  if (
    !supportsHomeScreenNotificationNotice(platform)
    || !authResolved
    || !settingsUrl
  ) {
    return null
  }
  if (user && browserSessionId) {
    return (
      <RegisteredHomeScreenNotificationNotice
        expectedUserId={user.id}
        expectedSessionId={browserSessionId}
        platform={platform}
        settingsUrl={settingsUrl}
      />
    )
  }
  return (
    <HomeScreenNotificationNotice
      platform={platform}
      setupState="needs-setup"
      settingsUrl={settingsUrl}
    />
  )
}
